import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { orderingProviderKeys } from 'src/constants/orders';
import { hasOrderingProviderChanges } from 'src/distribution_platforms/features/utils/ordering_provider_utils';
import { OrderingProviderFieldsFragment } from 'src/types';

export type UseOrderingProviderFormSectionParams = {
  ldtOrderRepository: LdtOrderRepository;
  isOrderingPhysicianUser: boolean;
  initialOrderingProvider: OrderingProviderFieldsFragment | null;
  setOrderingProviderId: (
    id: string,
    options?: { shouldValidate?: boolean; shouldTouch?: boolean },
  ) => void;
  triggerOrderingProviderValidation: () => Promise<boolean>;
};

export type UseOrderingProviderFormSectionResult = {
  selectedOrderingProvider: OrderingProviderFieldsFragment | null;
  hasOrderingProviderChanges: boolean;
  setSelectedOrderingProvider: (
    provider: OrderingProviderFieldsFragment | null,
  ) => void;
  isOrderingProviderFormOpen: boolean;
  setIsOrderingProviderFormOpen: (open: boolean) => void;
  editingProvider: OrderingProviderFieldsFragment | null;
  setEditingProvider: (provider: OrderingProviderFieldsFragment | null) => void;
  orderingProviderSectionProps: {
    onEditProvider: (provider: OrderingProviderFieldsFragment) => void;
    onChangeProvider: (provider: OrderingProviderFieldsFragment | null) => void;
    onClickAddProvider: () => void;
    canShowAddProviderButton: boolean;
  };
  orderingProviderDialogProps: {
    open: boolean;
    onClose: () => void;
    initialProvider: OrderingProviderFieldsFragment | null;
    onSuccess: (provider: OrderingProviderFieldsFragment) => Promise<void>;
    onDelete: (providerId: string) => Promise<void>;
  };
};

/**
 * Shared state and handlers for the Ordering Provider form section and dialog
 * used in LDT order forms (e.g. prostate biopsy, prostate post-RP).
 */
export function useOrderingProviderFormSection({
  ldtOrderRepository,
  isOrderingPhysicianUser,
  initialOrderingProvider,
  setOrderingProviderId,
  triggerOrderingProviderValidation,
}: UseOrderingProviderFormSectionParams): UseOrderingProviderFormSectionResult {
  const [isOrderingProviderFormOpen, setIsOrderingProviderFormOpen] =
    useState(false);
  const [editingProvider, setEditingProvider] =
    useState<OrderingProviderFieldsFragment | null>(null);
  const [selectedOrderingProvider, setSelectedOrderingProvider] =
    useState<OrderingProviderFieldsFragment | null>(initialOrderingProvider);
  const hasUserClosedFormRef = useRef(false);

  const hasOrderingProviderFieldChanges = useMemo(
    () =>
      hasOrderingProviderChanges(
        selectedOrderingProvider,
        initialOrderingProvider,
      ),
    [selectedOrderingProvider, initialOrderingProvider],
  );

  const {
    data: orderingProvidersForUser,
    isLoading: isLoadingOrderingProviders,
  } = useQuery({
    queryKey: orderingProviderKeys.all,
    queryFn: () =>
      ldtOrderRepository.getOrderingProviders({
        cursor: null,
        first: 1,
      }),
    enabled: isOrderingPhysicianUser,
  });

  const hasOrderingProviderForUser =
    (orderingProvidersForUser?.items?.length ?? 0) > 0;

  useEffect(() => {
    if (
      isOrderingPhysicianUser &&
      !isLoadingOrderingProviders &&
      !hasOrderingProviderForUser &&
      !isOrderingProviderFormOpen &&
      !initialOrderingProvider &&
      !hasUserClosedFormRef.current
    ) {
      setIsOrderingProviderFormOpen(true);
    }
  }, [
    isOrderingPhysicianUser,
    isLoadingOrderingProviders,
    hasOrderingProviderForUser,
    isOrderingProviderFormOpen,
    initialOrderingProvider,
  ]);

  const orderingProviderSectionProps = useMemo(
    () => ({
      onEditProvider: (provider: OrderingProviderFieldsFragment) => {
        setEditingProvider(provider);
        setIsOrderingProviderFormOpen(true);
      },
      onChangeProvider: (provider: OrderingProviderFieldsFragment | null) => {
        setSelectedOrderingProvider(provider);
      },
      onClickAddProvider: () => {
        setEditingProvider(null);
        setIsOrderingProviderFormOpen(true);
      },
      canShowAddProviderButton: !(
        isOrderingPhysicianUser &&
        (isLoadingOrderingProviders || hasOrderingProviderForUser)
      ),
    }),
    [
      isOrderingPhysicianUser,
      isLoadingOrderingProviders,
      hasOrderingProviderForUser,
    ],
  );

  const orderingProviderDialogProps = useMemo(
    () => ({
      open: isOrderingProviderFormOpen,
      onClose: () => {
        setIsOrderingProviderFormOpen(false);
        setEditingProvider(null);
        hasUserClosedFormRef.current = true;
      },
      initialProvider: editingProvider,
      onSuccess: async (provider: OrderingProviderFieldsFragment) => {
        setOrderingProviderId(provider.id, {
          shouldValidate: true,
          shouldTouch: true,
        });
        setSelectedOrderingProvider(provider);
        await triggerOrderingProviderValidation();
        setIsOrderingProviderFormOpen(false);
        setEditingProvider(null);
        hasUserClosedFormRef.current = false;
      },
      onDelete: async (providerId: string) => {
        if (selectedOrderingProvider?.id === providerId) {
          setOrderingProviderId('', {
            shouldValidate: true,
            shouldTouch: true,
          });
          setSelectedOrderingProvider(null);
          await triggerOrderingProviderValidation();
        }
      },
    }),
    [
      isOrderingProviderFormOpen,
      editingProvider,
      selectedOrderingProvider,
      setOrderingProviderId,
      triggerOrderingProviderValidation,
    ],
  );

  return {
    selectedOrderingProvider,
    hasOrderingProviderChanges: hasOrderingProviderFieldChanges,
    setSelectedOrderingProvider,
    isOrderingProviderFormOpen,
    setIsOrderingProviderFormOpen,
    editingProvider,
    setEditingProvider,
    orderingProviderSectionProps,
    orderingProviderDialogProps,
  };
}
